/**
 * Hook for managing workspace notification preferences
 *
 * Uses TanStack Query for server state management with optimistic updates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { NotificationPreferenceKey } from '@loader/shared-types';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { useToastStore } from '@/store/useToasts';

/**
 * Notification preferences data structure
 */
export interface NotificationPreferences {
  workspace_invitation: boolean;
  system_tag_changed: boolean;
  version_deleted: boolean;
  table_deleted: boolean;
  linked_table_deleted: boolean;
}

/**
 * Default notification preferences (all enabled)
 */
const DEFAULT_PREFERENCES: NotificationPreferences = {
  workspace_invitation: true,
  system_tag_changed: true,
  version_deleted: true,
  table_deleted: true,
  linked_table_deleted: true,
};

/**
 * Hook for fetching and updating notification preferences
 */
export function useNotificationPreferences() {
  const rpc = useRpc();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToastStore();

  // Query for fetching preferences
  const query = useQuery({
    queryKey: queryKeys.workspace.notificationPreferences,
    queryFn: async (): Promise<NotificationPreferences> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.me['notification-preferences'].$get();

      if (!res.ok) {
        throw new Error('Failed to fetch notification preferences');
      }

      const data = await res.json();

      if (data.preferences) {
        return {
          workspace_invitation: data.preferences.workspace_invitation ?? true,
          system_tag_changed: data.preferences.system_tag_changed ?? true,
          version_deleted: data.preferences.version_deleted ?? true,
          table_deleted: data.preferences.table_deleted ?? true,
          linked_table_deleted: data.preferences.linked_table_deleted ?? true,
        };
      }

      return DEFAULT_PREFERENCES;
    },
  });

  // Mutation for updating preferences
  const updateMutation = useMutation({
    mutationFn: async ({
      key,
      value,
    }: {
      key: NotificationPreferenceKey;
      value: boolean;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.me['notification-preferences'].$put({
        json: { [key]: value },
      });

      if (!res.ok) {
        throw new Error('Failed to update notification preference');
      }

      return { key, value };
    },
    onMutate: async ({ key, value }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.workspace.notificationPreferences,
      });

      // Snapshot the previous value
      const previousPreferences = queryClient.getQueryData<NotificationPreferences>(
        queryKeys.workspace.notificationPreferences
      );

      // Optimistically update to the new value
      queryClient.setQueryData<NotificationPreferences>(
        queryKeys.workspace.notificationPreferences,
        (old) => (old ? { ...old, [key]: value } : { ...DEFAULT_PREFERENCES, [key]: value })
      );

      return { previousPreferences };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousPreferences) {
        queryClient.setQueryData(
          queryKeys.workspace.notificationPreferences,
          context.previousPreferences
        );
      }
      showError('Failed to save', 'Could not update notification preference');
    },
    onSuccess: () => {
      showSuccess('Preferences Updated', 'Notification preference has been saved');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: queryKeys.workspace.notificationPreferences,
      });
    },
  });

  /**
   * Toggle a specific notification preference
   */
  const togglePreference = (key: NotificationPreferenceKey) => {
    const currentValue = query.data?.[key] ?? true;
    updateMutation.mutate({ key, value: !currentValue });
  };

  return {
    preferences: query.data ?? DEFAULT_PREFERENCES,
    isLoading: query.isLoading,
    isError: query.isError,
    isSaving: updateMutation.isPending,
    togglePreference,
  };
}
